/**
 * API服务层 - 双喜婚礼影像小程序
 * 与CMS后端 (http://localhost:5000/api) 通信
 */

// API基础URL - 从环境变量读取，默认为localhost:5001
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

/**
 * 通用请求处理函数
 * @param {string} url - API路径
 * @param {object} options - fetch选项
 * @returns {Promise<any>} - 返回解析后的JSON数据
 */
const request = async (url, options = {}) => {
  try {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: '请求失败' }));
      throw new Error(error.message || `HTTP错误: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API请求错误:', error);
    throw error;
  }
};

/**
 * 文件上传请求处理
 * @param {string} url - API路径
 * @param {FormData} formData - 表单数据
 * @returns {Promise<any>}
 */
const uploadRequest = async (url, formData) => {
  try {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'POST',
      body: formData,
      // 不设置Content-Type，让浏览器自动设置multipart/form-data边界
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: '上传失败' }));
      throw new Error(error.message || `上传错误: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('文件上传错误:', error);
    throw error;
  }
};

// ==================== 首页相关API ====================

/**
 * 获取首页所有数据（聚合接口）
 * @returns {Promise<object>} - { banners, categories, bottomBanner, featuredVideos }
 */
export const getHomeData = async () => {
  return request('/home/data');
};

/**
 * 获取首页Banner列表
 * @returns {Promise<Array>}
 */
export const getHomeBanners = async () => {
  return request('/home/banners');
};

/**
 * 获取首页横滑分类
 * @returns {Promise<Array>}
 */
export const getHomeCategories = async () => {
  return request('/home/categories');
};

/**
 * 获取首页精选视频
 * @returns {Promise<Array>}
 */
export const getHomeFeaturedVideos = async () => {
  return request('/home/featured-videos');
};

/**
 * 获取首页底部Banner
 * @returns {Promise<object>}
 */
export const getHomeBottomBanner = async () => {
  return request('/home/bottom-banners');
};

// ==================== 作品相关API ====================

/**
 * 获取所有作品分类
 * @returns {Promise<Array>}
 */
export const getWorkCategories = async () => {
  return request('/works/categories');
};

/**
 * 获取作品分类页面配置（婚礼/商业两个面板）
 * @returns {Promise<Array>}
 */
export const getWorksCategoryPanels = async () => {
  return request('/categories/works-category-panels');
};

/**
 * 根据slug获取分类详情
 * @param {string} slug - 分类slug
 * @returns {Promise<object>}
 */
export const getCategoryBySlug = async (slug) => {
  return request(`/categories/slug/${slug}`);
};

/**
 * 获取指定分类的作品列表
 * @param {string} categoryId - 分类ID
 * @param {object} params - 查询参数 { page, limit, subCategory }
 * @returns {Promise<object>} - { works, total, page, totalPages }
 */
export const getWorksByCategory = async (categoryId, params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const url = `/works/category/${categoryId}${queryString ? `?${queryString}` : ''}`;
  return request(url);
};

/**
 * 获取单个作品详情
 * @param {string} workId - 作品ID
 * @returns {Promise<object>}
 */
export const getWorkDetail = async (workId) => {
  return request(`/works/${workId}`);
};

/**
 * 获取所有作品（带过滤）
 * @param {object} params - { page, limit, categoryType, subCategory, featured }
 * @returns {Promise<object>}
 */
export const getWorks = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return request(`/works${queryString ? `?${queryString}` : ''}`);
};

// ==================== 发现精彩相关API ====================

/**
 * 获取发现页视频列表
 * @param {object} params - { category, page, limit }
 * @returns {Promise<object>} - { videos, total, page, totalPages }
 */
export const getDiscoverVideos = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return request(`/discover${queryString ? `?${queryString}` : ''}`);
};

/** 获取由 CMS 管理的发现页分类 */
export const getDiscoverCategories = async () => {
  return request('/discover/categories');
};

/**
 * 获取指定分类的发现视频
 * @param {string} category - 分类（品牌故事、商业项目、纪实现场、创意短片或婚礼影像）
 * @returns {Promise<Array>}
 */
export const getDiscoverVideosByCategory = async (category) => {
  return request(`/discover?category=${encodeURIComponent(category)}`);
};

/**
 * 获取发现页统计数据
 * @returns {Promise<object>} - { totalVideos, totalViews, categoryStats }
 */
export const getDiscoverStats = async () => {
  return request('/discover/stats');
};

// ==================== 店铺信息相关API ====================

/**
 * 获取店铺信息
 * @returns {Promise<object>} - { name, address, contactNumber, wechatAccount, images, businessHours }
 */
export const getStoreInfo = async () => {
  return request('/store');
};

// ==================== 预约相关API ====================

/**
 * 提交预约表单
 * @param {object} bookingData - 预约表单数据
 * @returns {Promise<object>} - { success, bookingId, message }
 */
export const submitBooking = async (bookingData) => {
  return request('/bookings', {
    method: 'POST',
    body: JSON.stringify(bookingData),
  });
};

/**
 * 获取预约详情（用于确认页面）
 * @param {string} bookingId - 预约ID
 * @returns {Promise<object>}
 */
export const getBookingDetail = async (bookingId) => {
  return request(`/bookings/${bookingId}`);
};

/**
 * 更新预约信息（用户修改预约）
 * @param {string} bookingId - 预约ID
 * @param {object} updateData - 更新数据
 * @returns {Promise<object>}
 */
export const updateBooking = async (bookingId, updateData) => {
  return request(`/bookings/${bookingId}`, {
    method: 'PUT',
    body: JSON.stringify(updateData),
  });
};

// ==================== 文件上传API ====================

/**
 * 上传单个文件
 * @param {File} file - 文件对象
 * @returns {Promise<object>} - { url, filename, size }
 */
export const uploadSingleFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return uploadRequest('/upload/single', formData);
};

/**
 * 上传多个文件
 * @param {File[]} files - 文件数组
 * @returns {Promise<object>} - { files: [{url, filename, size}] }
 */
export const uploadMultipleFiles = async (files) => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });
  return uploadRequest('/upload/multiple', formData);
};

// ==================== 工具函数 ====================

/**
 * 构建完整的媒体文件URL
 * @param {string} path - 文件路径（相对路径）
 * @returns {string} - 完整URL
 */
export const getMediaUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  // 移除开头的斜杠（如果有）
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  const mediaBaseUrl = API_BASE_URL.replace(/\/api\/?$/, '');
  return `${mediaBaseUrl}/${cleanPath}`;
};

/**
 * 格式化时长（秒 -> MM:SS）
 * @param {number} seconds - 秒数
 * @returns {string} - 格式化后的时长
 */
export const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * 格式化播放量/浏览量
 * @param {number} count - 数量
 * @returns {string} - 格式化后的数量（如：1.2k, 3.5w）
 */
export const formatViewCount = (count) => {
  if (count >= 10000) {
    return `${(count / 10000).toFixed(1)}w`;
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}k`;
  }
  return count.toString();
};

// 导出API基础URL（用于调试）
export { API_BASE_URL };

// 默认导出所有API函数
const api = {
  // 首页
  getHomeData,
  getHomeBanners,
  getHomeCategories,
  getHomeFeaturedVideos,
  getHomeBottomBanner,

  // 作品
  getWorkCategories,
  getWorksCategoryPanels,
  getCategoryBySlug,
  getWorksByCategory,
  getWorkDetail,
  getWorks,

  // 发现
  getDiscoverVideos,
  getDiscoverVideosByCategory,
  getDiscoverStats,

  // 店铺
  getStoreInfo,

  // 预约
  submitBooking,
  getBookingDetail,
  updateBooking,

  // 上传
  uploadSingleFile,
  uploadMultipleFiles,

  // 工具
  getMediaUrl,
  formatDuration,
  formatViewCount,
};

export default api;
